/**
 * Markdown content preprocessing utilities.
 *
 * Pure string transformations applied to AI-generated markdown content
 * before passing to ReactMarkdown for rendering. Used by both the chat
 * (AIResponse) and project asset panels (MarkdownRenderer).
 */

/**
 * Remove emoji characters from text.
 * AI responses sometimes include emojis that distract from technical content.
 *
 * Uses the Unicode Extended_Pictographic property (ES2018+) to cover all
 * current and future emoji additions without maintaining a manual range list.
 * Also handles compound emojis joined with zero-width joiners (ZWJ sequences)
 * and variation selectors (U+FE0F) as single units.
 */
export const removeEmojis = (text: string): string => {
  return text.replace(
    /\p{Extended_Pictographic}(\u{FE0F}|\u{200D}\p{Extended_Pictographic})*/gu,
    "",
  );
};

/**
 * Remove broken markdown image references that have no URL.
 * AI sometimes outputs `![Alt Text]` without a `(url)` part, which renders
 * as literal "![Alt Text]" text. This strips those when they appear on their own line.
 */
export const removeBrokenImageRefs = (text: string): string => {
  return text.replace(/^!\[[^\]]*\]\s*$/gm, "");
};

/**
 * Strip HTML comments (e.g. `<!-- SECTION:Description -->`) that ReactMarkdown
 * cannot parse. The actual markdown headings are already in the content.
 */
export const stripHtmlComments = (text: string): string => {
  return text.replace(/<!--[\s\S]*?-->/g, "");
};

/**
 * Apply all standard preprocessing transformations for chat content.
 * Composes: removeEmojis → removeBrokenImageRefs
 */
export const preprocessChatContent = (content: string): string => {
  return removeBrokenImageRefs(removeEmojis(content));
};

/**
 * Apply all standard preprocessing transformations for project asset content.
 * Composes: stripHtmlComments
 */
export const preprocessAssetContent = (content: string): string => {
  return stripHtmlComments(content);
};

/** Matches an ATX heading line, capturing its level and title. */
const HEADING_LINE = /^(#{1,6})\s+(.*)$/;

/**
 * An opening or closing code fence. Backticks or tildes, both of which markdown
 * accepts, and indented fences are matched too since agents often indent them.
 */
const FENCE_LINE = /^\s*(?:```|~~~)/;

/**
 * Normalises a heading title for comparison: trims, collapses internal
 * whitespace, strips trailing `#`s and markdown emphasis, and lower-cases.
 * The agent authors these headings by hand, so exact-match would be brittle.
 */
const normalizeHeading = (title: string): string =>
  title
    .replace(/#+\s*$/, "")
    .replace(/[*_`]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

/**
 * Extracts the body of a markdown section addressed by its heading title.
 *
 * Returns everything after the matching heading up to the next heading at the
 * same or a higher level (so nested subsections are included), with surrounding
 * blank lines trimmed. Returns `undefined` when no heading matches — callers
 * should treat that as "the author didn't write this section" and render
 * nothing, rather than showing an empty state that implies it's set but blank.
 *
 * Heading level is inferred from whichever heading matches, so the caller does
 * not need to know whether the author used `##` or `###`.
 *
 * @param markdown - the full document
 * @param heading - the heading title to find, compared case- and
 *   emphasis-insensitively
 * @returns the section body, or `undefined` if the heading is absent
 */
export const extractSection = (
  markdown: string | undefined,
  heading: string,
): string | undefined => {
  if (!markdown) return undefined;

  const target = normalizeHeading(heading);
  const lines = markdown.split("\n");

  // Collect the real headings in a single pass, tracking fenced code blocks so a
  // line like `## deploy` or `# comment` inside a fence is not mistaken for one.
  // Done once rather than per-scan because the fence state has to be shared: a
  // fence opened before the target heading affects where the section ends, and
  // two independent scans would disagree about it.
  //
  // An unterminated fence swallows the rest of the document, which matches how a
  // markdown renderer would treat it.
  const headings: Array<{ line: number; level: number; title: string }> = [];
  let inFence = false;

  for (let i = 0; i < lines.length; i++) {
    if (FENCE_LINE.test(lines[i])) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    const match = HEADING_LINE.exec(lines[i]);
    if (match) {
      headings.push({ line: i, level: match[1].length, title: match[2] });
    }
  }

  const startAt = headings.findIndex(
    (h) => normalizeHeading(h.title) === target,
  );
  if (startAt === -1) return undefined;

  const section = headings[startAt];
  // Ends at the next heading of the same or higher level, so nested subsections
  // stay part of the body.
  const next = headings.find((h, i) => i > startAt && h.level <= section.level);

  return lines
    .slice(section.line + 1, next ? next.line : lines.length)
    .join("\n")
    .trim();
};
